import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../supabaseClient';
import { getActiveTeamId, setActiveTeamId } from '../activeTeam';
import type { Profile } from '../tareas/types';
import { setChatUser } from '../chatWidget';

export interface TeamLite {
  id: string;
  nombre: string;
  role: 'owner' | 'admin' | 'member';
  logoUrl: string | null;
}

interface AuthContextValue {
  session: Session | null;
  user: Session['user'] | null;
  profile: Profile | null;
  loading: boolean;
  // Espacios de trabajo a los que pertenece el usuario.
  teams: TeamLite[];
  // Espacio activo de ESTA pestaña (null hasta resolverse o si no hay equipos).
  activeTeamId: string | null;
  switchTeam: (id: string) => void;
  // Sin ningún equipo todavía: hay que enseñar el onboarding.
  needsOnboarding: boolean;
  signOut: () => Promise<unknown>;
  reloadProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface Props {
  children: ReactNode;
}

export function AuthProvider({ children }: Props) {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [teams, setTeams] = useState<TeamLite[]>([]);
  const [activeTeamId, setActive] = useState<string | null>(getActiveTeamId());
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  // Resuelve el espacio activo de la pestaña a partir de las membresías:
  // 1) el de sessionStorage si sigue siendo válido; 2) el "por defecto"
  // (profile.team_id); 3) el primero. Lo escribe en sessionStorage para que la
  // cabecera x-team-id esté presente desde la primera consulta de datos.
  const resolveActive = useCallback((list: TeamLite[], defaultTeam: string | null) => {
    const ids = list.map((t) => t.id);
    const stored = getActiveTeamId();
    const next =
      stored && ids.includes(stored) ? stored
        : defaultTeam && ids.includes(defaultTeam) ? defaultTeam
          : ids[0] ?? null;
    setActiveTeamId(next);
    setActive(next);
  }, []);

  const loadAll = useCallback(async (userId: string) => {
    const [{ data: prof }, { data: mems }] = await Promise.all([
      supabase.from('profiles').select('id, nombre, color, team_id').eq('id', userId).single(),
      supabase.from('team_members').select('role, team:teams(id, nombre, logo_url)').eq('user_id', userId),
    ]);
    const p = (prof as Profile | null) ?? null;
    type TeamEmbed = { id: string; nombre: string; logo_url: string | null };
    type MemRow = { role: 'owner' | 'admin' | 'member'; team: TeamEmbed | TeamEmbed[] | null };
    const list: TeamLite[] = ((mems ?? []) as unknown as MemRow[])
      .map((m) => {
        const t = Array.isArray(m.team) ? m.team[0] : m.team;
        return t ? { id: t.id, nombre: t.nombre, role: m.role, logoUrl: t.logo_url ?? null } : null;
      })
      .filter((x): x is TeamLite => x != null)
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
    setProfile(p);
    setTeams(list);
    resolveActive(list, p?.team_id ?? null);
    setDataLoaded(true);
  }, [resolveActive]);

  useEffect(() => {
    if (session?.user?.id) {
      loadAll(session.user.id);
    } else {
      setProfile(null);
      setTeams([]);
      setActive(null);
      setActiveTeamId(null);
      setDataLoaded(false);
    }
  }, [session, loadAll]);

  // Widget de chat: la primera llamada hace el init (anónimo o con el usuario
  // logueado, una vez cargado el perfil para pasar el nombre); las siguientes
  // identifican en login / vuelven a anónimo en logout, sin recargar.
  useEffect(() => {
    if (session === undefined) return;
    if (!session) { setChatUser(null); return; }
    if (!dataLoaded) return;
    setChatUser({
      id: session.user.id,
      name: profile?.nombre || session.user.email || '',
      email: session.user.email ?? '',
    });
  }, [session, dataLoaded, profile]);

  const switchTeam = useCallback((id: string) => {
    setActiveTeamId(id);
    setActive(id);
    // Recordar como espacio por defecto (best-effort; no bloquea el cambio).
    supabase.rpc('set_default_team', { p_team_id: id }).then(() => {}, () => {});
  }, []);

  const signOut = useCallback(() => supabase.auth.signOut(), []);

  const value: AuthContextValue = {
    session: session ?? null,
    user: session?.user ?? null,
    profile,
    loading: session === undefined,
    teams,
    activeTeamId,
    switchTeam,
    needsOnboarding: !!session && dataLoaded && teams.length === 0,
    signOut,
    reloadProfile: async () => { if (session?.user?.id) await loadAll(session.user.id); },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  return ctx;
}
